// src/Reuse/FormInput/StyledSelectInput.js
import React, { memo } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { IconButton, Stack, Typography } from "@mui/material";
import { Controller } from "react-hook-form";
import StyleStack from "../UI/StyleStack";
import { CheckValue } from "../../Service/service";
import { DroneIcon } from "../../Service/Constats";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import StyledFormControl from "./StyledFormControl";
import { StyledSelectField, StyledSelectItem } from "./StyledSelectField";
import StyledTextField from "./StyledTextField";
import StyledToolTip from "../UI/StyledToolTip";

/**
 * Controller-integrated StyledSelectInput for react-hook-form
 */
const StyledSelectInput = ({
  name,
  control,
  rules = {},
  defaultValue = "",
  showError = true,
  sx = {},
  onChange = null,
  label,
  optionList = [],
  ...props
}) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState, formState: { name: fieldName } }) => {
        const { error } = fieldState;
        const {
          showLabel = true,
          placeholder = "",
          helperText = error?.message || props.helperText,
          multiple = false,
        } = props;

        return (
          <StyledFormControl
            label={label}
            id={name}
            error={!!error}
            helperText={showError ? helperText : null}
            showLabel={showLabel}
            sx={sx}
          >
            <StyledToolTip
              title={showError ? "" : helperText || ""}
              arrow
              placement="top"
            >
              <StyledSelectField
                {...field}
                {...props}
                error={!!error}
                IconComponent={KeyboardArrowDownIcon}
                input={<StyledTextField />}
                displayEmpty
                variant="outlined"
                value={field.value ?? (multiple ? [] : "")}
                onChange={(e) => {
                  const value = e.target.value;
                  if (!!onChange) {
                    onChange(value, field.onChange, fieldName);
                  } else {
                    field.onChange(value);
                  }
                }}
                renderValue={(selected) => {
                  if (!selected || !optionList.length || !selected.length) {
                    return (
                      <Typography
                        sx={(theme) => ({ color: theme.palette.grey[500] })}
                      >
                        {placeholder}
                      </Typography>
                    );
                  }

                  if (typeof selected === "string") {
                    const match = optionList.find((o) => o.value === selected);
                    return match?.type === "drone_type" ? (
                      <DroneTypeSelect {...match} sx={{ border: 0 }} />
                    ) : (
                      <Typography variant="subtitle2">
                        {match?.label || selected}
                      </Typography>
                    );
                  }

                  if (Array.isArray(selected)) {
                    return (
                      <Stack direction="row" gap={1} sx={{ flexWrap: "wrap" }}>
                        {selected.map((item, idx) => {
                          const match = optionList.find(
                            (opt) => opt.value === item,
                          );
                          return (
                            <React.Fragment key={idx}>
                              {match?.type === "drone_type" ? (
                                <DroneTypeSelect {...match} />
                              ) : (
                                <Typography variant="subtitle2">
                                  {match?.label || item}
                                </Typography>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </Stack>
                    );
                  }

                  return selected?.label || selected;
                }}
                sx={{
                  ...sx,
                  background: showLabel ? "inherit" : "#ffffff",
                }}
                aria-description={`${name}-helper-text`}
              >
                <StyledSelectItem disabled value="">
                  <em>{placeholder}</em>
                </StyledSelectItem>
                {optionList.map((option, index) => (
                  <StyledSelectItem
                    key={option.value + index}
                    value={option.value}
                  >
                    <DroneTypeSelect
                      {...option}
                      sx={{ border: 0, background: "transparent" }}
                    />
                  </StyledSelectItem>
                ))}
              </StyledSelectField>
            </StyledToolTip>
          </StyledFormControl>
        );
      }}
    />
  );
};

export default memo(StyledSelectInput);

const DroneTypeSelect = memo(
  ({ icon, type, label, value, showClose = false, sx = {} }) => {
    return (
      <StyleStack
        key={value}
        direction="row"
        gap={0.5}
        alignItems="center"
        sx={{ borderRadius: 0.75, padding: 0.5, ...sx }}
      >
        {CheckValue(icon) && type === "drone_type" && (
          <DroneIcon icon={icon} sx={{ fontSize: 11 }} />
        )}
        <Typography variant="subtitle2">{label}</Typography>
        {showClose && (
          <IconButton sx={{ width: 14, height: 14 }}>
            <ClearRoundedIcon sx={{ fontSize: 10 }} />
          </IconButton>
        )}
      </StyleStack>
    );
  },
);
